import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Question } from '../models/question.interface';

declare const ttq: {
  identify: (payload: Record<string, string>) => void;
  track: (event: string, payload: Record<string, unknown>) => void;
} | undefined;

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private apiUrl = environment.baseUrl + 'onboarding/';
  private onbTokenSubject = new BehaviorSubject<string | null>(null);
  onbToken$ = this.onbTokenSubject.asObservable();
  private quizCache: { questions: Question[]; cryptoQuestionsCount: number } | null = null;
  private pixelData: { email?: string; phone?: string; externalId?: string } = {};

  constructor(private http: HttpClient) {}

  setPixelEmail(email: string) { this.pixelData.email = email; }
  setPixelPhone(phone: string) { this.pixelData.phone = phone; }
  setPixelExternalId(id: string) { this.pixelData.externalId = id; }
  getPixelData() { return { ...this.pixelData }; }
  clearPixelData() { this.pixelData = {}; }

  async trackTikTokCompleteRegistration(): Promise<void> {
    try {
      if (typeof ttq === 'undefined') {
        return;
      }

      const pixelData = this.getPixelData();
      const identifyPayload: Record<string, string> = {};

      if (pixelData.email) {
        identifyPayload['email'] = await this.hashSHA256(pixelData.email);
      }
      if (pixelData.phone) {
        identifyPayload['phone_number'] = await this.hashSHA256(pixelData.phone);
      }
      if (pixelData.externalId) {
        identifyPayload['external_id'] = await this.hashSHA256(
          pixelData.externalId,
        );
      }

      if (Object.keys(identifyPayload).length > 0) {
        ttq.identify(identifyPayload);
      }

      ttq.track('CompleteRegistration', {});
    } catch (error) {
      console.error('TikTok pixel error:', error);
    } finally {
      this.clearPixelData();
    }
  }

  private async hashSHA256(value: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(value);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  setOnbToken(token: string | null) {
    this.onbTokenSubject.next(token);
  }

  getOnbToken(): Observable<string | null> {
    return this.onbToken$;
  }

  getQuestions() {
    return this.http.get(this.apiUrl + 'questions');
  }

  setQuizCache(questions: Question[], cryptoQuestionsCount: number) {
    this.quizCache = { questions, cryptoQuestionsCount };
  }

  getQuizCache(): { questions: Question[]; cryptoQuestionsCount: number } | null {
    return this.quizCache;
  }

  clearQuizCache() {
    this.quizCache = null;
  }

  reviewQuiz(answers: any[]) {
    return this.http.post(this.apiUrl + 'review', answers);
  }

  setTermsAndConditions() {
    return this.http.put(this.apiUrl + 'terms', null);
  }

  sendOtp(data: string, channel: 'SMS' | 'EMAIL') {
    const params = {
      channel,
      phoneData: channel === 'SMS' ? data : null,
      emailData: channel === 'EMAIL' ? data : null,
    };
    return this.http.post(this.apiUrl + 'send-otp', params);
  }

  validateOtp(code: string, channel: 'SMS' | 'EMAIL') {
    const params = {
      channel,
      otpNumber: code,
    };
    return this.http.put(this.apiUrl + 'valid-otp', params);
  }
}
